import { List, Map } from 'immutable';
import { latexToAsciimath } from 'phoenix2/utils/asciimath';

export function checkQuestion(question) {
    const result = question.get('steps').every(checkStep);
    return result;
}

function checkStep(step, index) {
    const input = step.get('input');
    const result =
        checkChoiceGroups(input) &&
        checkNumberFields(input) &&
        checkExpressionFields(input) &&
        checkSlotGroups(input);
    return result;
}

function checkChoiceGroups(input) {
    const choices = input.get('choices');
    return input.get('choice_groups').every(group => checkChoiceGroup(group, choices));
}

function checkChoiceGroup(group, choices) {
    const minChoices = group.get('min_choices');
    const maxChoices = group.get('max_choices');
    const selectedCount = group
        .get('choice_refs')
        .filter(ref => choices.getIn([ref, 'selected']))
        .count();
    return selectedCount >= minChoices && selectedCount <= maxChoices;
}

function isValidNumber(value) {
    return value && /^(-|\+)?([0-9]+(\.[0-9]+)?|Infinity)$/.test(value);
}

function checkNumberFields(input) {
    const result = input.get('number_fields').every(field => isValidNumber(field.get('value')));
    return result;
}

export function isExpressionValid(value) {
    // Validate the expression as asciimath
    const avalue = latexToAsciimath(value);
    return !!avalue && avalue.trim().length > 0 && !/\(\s+\)/.test(avalue); // TODO: do some more validation
}

function checkExpressionFields(input) {
    const result = input
        .get('expression_fields', [])
        .every(field => isExpressionValid(field.get('value')));
    return result;
}

function checkSlotGroups(input) {
    const slots = input.get('slots');
    return input.get('slot_groups').every(group => checkSlotGroup(group, slots));
}

function checkSlotGroup(group, slots) {
    return group.get('slot_refs').every(ref => slots.getIn([ref, 'card_ref']));
}

export function buildQuestionAnswers(question) {
    return question
        .get('steps')
        .map(buildStepAnswer)
        .toArray();
}

function buildStepAnswer(step) {
    const input = step.get('input');
    const answers = {};
    buildNumberFieldAnswers(input, answers);
    buildExpressionFieldAnswers(input, answers);
    buildChoiceAnswers(input, answers);
    buildSlotAnswers(input, answers);
    return answers;
}

function buildNumberFieldAnswers(input, acc) {
    input.get('number_fields').forEach((value, key) => {
        acc[key] = { text: value.get('value') }; // what is acc??
    });
}

function buildExpressionFieldAnswers(input, acc) {
    input.get('expression_fields', []).forEach((value, key) => {
        acc[key] = { text: latexToAsciimath(value.get('value')) }; // what is acc??
    });
}

function fillAnswerContent(value, answerData) {
    if (!answerData) {
        answerData = {};
    }
    const firstContent = value.getIn(['content', 0]);
    switch (firstContent.get('element')) {
        case 'text':
            answerData['text'] = firstContent.get('text');
            break;
        case 'image':
            answerData['image'] = firstContent.get('src');
            break;
    }
    return answerData;
}

function buildChoiceAnswers(input, acc) {
    input.get('choices').forEach((value, key) => {
        if (!value.get('selected')) {
            return;
        }
        acc[key] = fillAnswerContent(value);
    });
}

function buildSlotAnswers(input, acc) {
    const cards = input.get('cards');
    input.get('slots').forEach((value, key) => {
        const cardRef = value.get('card_ref');
        acc[key] = fillAnswerContent(cards.get(cardRef), { card_ref: cardRef });
    });
}

/*
 *  Question input mutation
 */

/**
 * Returns the choice group id given a choice ref. It returns undefined if
 * the group cannot be found.
 */
function getChoiceGroup(input, choiceId) {
    for (const [key, group] of input.get('choice_groups')) {
        if (group.get('choice_refs').find(ref => ref === choiceId)) {
            return key;
        }
    }
    return undefined; // unknown choice group
}

/**
 * Select a choice answer, resetting all of the other choices in the same group to unselected.
 * @param input - input object
 * @param choiceId - id of the choice element to change the status of
 * @param selected - if not undefined, will set the value of the choice id to the
 * specified value. If the value is undefined then the choice is set to selected
 */
export function selectChoice(input, choiceId, selected) {
    const choiceGroupId = getChoiceGroup(input, choiceId);
    return input.set(
        'choices',
        input.get('choices').map((c, k) => {
            // Only change the selected in the same choice group
            if (getChoiceGroup(input, k) === choiceGroupId) {
                // Each choice has is deselected unless its id is choiceId
                let value = k === choiceId;
                if (value && (selected === true || selected === false)) {
                    value = selected;
                }
                return c.set('selected', value);
            }
            return c;
        }),
    );
}

export function toggleChoice(input, choiceId) {
    return input.updateIn(['choices', choiceId, 'selected'], val => !val);
}

export function setNumericAnswer(input, nfId, value) {
    return input.setIn(['number_fields', nfId, 'value'], value);
}

export function getNumericAnswer(input, nfId) {
    return input.getIn(['number_fields', nfId, 'value']);
}

export function setExpressionAnswer(input, nfId, value) {
    return input.setIn(['expression_fields', nfId, 'value'], value);
}

export function getExpressionAnswer(input, nfId) {
    return input.getIn(['expression_fields', nfId, 'value']);
}

export function selectCard(input, cardId) {
    const selectedCardId = input.get('selected_card');
    if (!selectedCardId) {
        return input.set('selected_card', cardId);
        // return false;
    }
    input = input.set('selected_card', null);
    if (selectedCardId === cardId) {
        return slotCard(input, cardId, null);
        // return true
    }
    const slotId = input.getIn(['cards', cardId, 'slot_ref']);
    const selectedSlotId = input.getIn(['cards', selectedCardId, 'slot_ref']);
    if (slotId || selectedSlotId) {
        input = slotCard(input, selectedCardId, slotId);
        return slotCard(input, cardId, selectedSlotId);
    } else {
        return input.set('selected_card', cardId);
        // return false;
    }
}

export function clearSelectedCard(input) {
    return input.set('selected_card', null);
}

export function dragSelectedCard(input, slotId) {
    const selectedCardId = input.get('selected_card');
    return slotCard(input, selectedCardId, slotId).delete('selected_card');
}

function slotCard(input, cardId, slotId) {
    let cardSlotId = null;
    if (cardId) {
        cardSlotId = input.getIn(['cards', cardId, 'slot_ref']);
        if (cardSlotId) {
            input = input.deleteIn(['slots', cardSlotId, 'card_ref']);
        }
        input = input.setIn(['cards', cardId, 'slot_ref'], slotId);
    }
    if (slotId) {
        const slottedCardId = input.getIn(['slots', slotId, 'card_ref']);
        if (slottedCardId) {
            // Is the card being put in the slot already in a different slot?
            // If so, we want to swap the cards, otherwise just remove the
            // card already in the slot:
            if (cardSlotId) {
                input = input.setIn(['cards', slottedCardId, 'slot_ref'], cardSlotId);
                input = input.setIn(['slots', cardSlotId, 'card_ref'], slottedCardId);
            } else {
                input = input.deleteIn(['cards', slottedCardId, 'slot_ref']);
            }
        }
        input = input.setIn(['slots', slotId, 'card_ref'], cardId);
    }
    return input;
}

/*
 * Building input from answer
 */

export function buildInputFromAnswer(input, answer, block) {
    const number_fields = buildNFsFromAnswer(input.get('number_fields'), answer, block);
    const expr_fields = buildNFsFromAnswer(input.get('expression_fields', []), answer, block);
    const choices = buildChoicesFromAnswer(input.get('choices'), answer, block);
    const [cards, slots] = buildCardsSlotsFromAnswer(
        input.get('cards'),
        input.get('slots'),
        answer,
        block,
    );
    return input.merge({ number_fields, expr_fields, choices, cards, slots });
}

function buildNFsFromAnswer(numberFields, answer, block) {
    return numberFields.map((v, k) => {
        if (answer.has(k)) {
            v = v.set('value', answer.get(k));
        }
        if (block) {
            v = v.set('blocked', true);
        }
        return v;
    });
}

function buildChoicesFromAnswer(choices, answer) {
    return choices.map((v, k) => (answer.has(k) ? v.set('selected', true) : v));
}

function buildCardsSlotsFromAnswer(cards, slots, answer) {
    const sc = {};
    slots = slots.map((v, k) => {
        if (answer.has(k)) {
            v = v.set('card_ref', answer.get(k));
        }
        sc[answer.get(k)] = k;
        return v;
    });
    cards = cards.map((v, k) => (sc[k] ? v.set('slot_ref', sc[k]) : v));
    return [cards, slots];
}

/**
 * Given a layout element for a question, search recursively to find the asset ids of question
 * images (i.e. 'figure' elements):
 * @param question an immutable section of a question layout
 * return an array of strings with the asset ids of the question images
 */
const getQuestionImages = question => {
    if (!question) {
        return [];
    }
    if (List.isList(question)) {
        return question.reduce((images, c) => {
            images.push(...getQuestionImages(c));
            return images;
        }, []);
    }
    if (Map.isMap(question)) {
        const image = question.getIn(['figure', 'image']);
        if (image) {
            return [image];
        }
        if (question.has('layout')) {
            return getQuestionImages(question.get('layout'));
        }
        if (question.has('content')) {
            return getQuestionImages(question.get('content'));
        }
    }
    return [];
};

/**
 * Given a layout element for a question, search recursively to find the asset id of the question
 * image (i.e. 'figure' elements). This method assumes that all questions can have at most one image,
 * which is true at the moment, but may not be in the future.
 * @param question an immutable section of a question layout
 * return a string containing the asset id if the question has an image, or undefined otherwise.
 */
export const getQuestionImage = question => {
    const images = getQuestionImages(question);
    return images && images.length > 0 ? images[0] : undefined;
};
